import debounce from 'lodash/debounce';
import * as React from 'react';
import { getCurrencySymbol } from '../../helpers/currency-format';
import { Html } from '../../helpers/html-helpers';


export interface IPriceRangeProps {
    onPriceRangeClear: (minPrice: number, maxPrice: number) => any;
    onSelectMinPrice: (number) => any;
    onSelectMaxPrice: (number) => any;
    lowerRangeLimit: number;
    upperRangeLimit: number;
    rangeMinSpread: number;
    startingMinPrice: number;
    startingMaxPrice: number;

    currencyFormat: string;
    clearButton: string;
    filterPriceRangeTitle: string;
    filterPriceRangeMessage: string;
    filterPriceRangeMin: string;
    filterPriceRangeMax: string;

    showClearButton: boolean;
    showTitle: boolean;
    /**
     * If supplied, the title of this filter will be overriden by this text.
     */
    titleText?: string;
}

interface IPriceRangeState {
    currentMinValue: number
    currentMaxValue: number,
    // separating slider value and text box value as onInput and onChange are the same in React Synthetic Events
    minTextBoxValue: number,
    maxTextBoxValue: number,
    rangeErrorMessage: string,
    rangeDifference: number,
    rangeMinError: boolean,
    rangeMaxError: boolean

}

class PriceRange extends React.PureComponent<IPriceRangeProps, IPriceRangeState>{
    actualWidth: number;
    actualX: number;
    actualY: number;

    constructor(props: IPriceRangeProps) {
        super(props);
        // debouncing functions triggered during input box change as
        // onInput and onChange are the same (to browser onInput) in React Synthetic Events

        this._onLowerRangeInputChange = debounce(this._onLowerRangeInputChange, 900);
        this._onUpperRangeInputChange = debounce(this._onUpperRangeInputChange, 900);

        this.state = {
            currentMinValue: props.startingMinPrice,
            currentMaxValue: props.startingMaxPrice,
            // separating slider value and text box value as onInput and onChange are the same in React Synthetic Events
            minTextBoxValue: props.startingMinPrice,
            maxTextBoxValue: props.startingMaxPrice,
            rangeErrorMessage: '',
            rangeMinError: false,
            rangeMaxError: false,
            rangeDifference: Math.ceil((this.props.rangeMinSpread / 100) * (this.props.upperRangeLimit - this.props.lowerRangeLimit))
        };
    }

    render() {
        const rangeMaxSpread = (this.props.upperRangeLimit - this.props.lowerRangeLimit) / 2;
        var leftSliderValue = ((this.state.currentMinValue - this.props.lowerRangeLimit) / (this.props.upperRangeLimit - this.props.lowerRangeLimit)) * 100;
        if (leftSliderValue < 0)
            leftSliderValue = 0;
        var priceRangeWidth = ((this.state.currentMaxValue - this.state.currentMinValue) / (this.props.upperRangeLimit - this.props.lowerRangeLimit)) * 100;
        priceRangeWidth = priceRangeWidth > 100 ? 100 : priceRangeWidth;
        const errorSvg = '<use xlink:href="#icon-alert" />';

        var currencySymbol = getCurrencySymbol(this.props.currencyFormat, this.props.lowerRangeLimit);

        return (
            <>
                {
                    this.props.showTitle ?
                        <div className="filters-section-heading contains-btn">
                            <h2 className="heading-general">{this.props.titleText ? this.props.titleText : this.props.filterPriceRangeTitle}</h2>
                            {
                                this.props.clearButton.length > 0 && this.props.showClearButton ?
                                    <button className="filters-section-heading-btn"
                                        value={this.props.clearButton + ' ' + this.props.titleText ? this.props.titleText : this.props.filterPriceRangeTitle}
                                        onClick={this._onPriceRangeClear.bind(this)}>
                                        {this.props.clearButton}
                                        <span className="visually-hidden"> {this.props.titleText ? this.props.titleText : this.props.filterPriceRangeTitle}</span>
                                    </button>
                                    : null
                            }
                        </div>
                        : null
                }

                <div className="filters-section-message">
                    {Html.renderDiv(this.props.filterPriceRangeMessage, "caption-paragraph")}
                </div>
                {this.props.children}
                <div className="price-range-container" onMouseOver={(evt) => { this._onPriceRangeClick(evt) }}>
                    <div className="price-range-spread" style={{
                        zIndex: 12,
                        left: "calc(" + (leftSliderValue + "%") + ")", width: "calc(" + priceRangeWidth + "%)"
                    }}>
                    </div>
                    <label htmlFor="slider-min" className="visually-hidden">{this.props.filterPriceRangeMin}</label>
                    <input role="slider" type="range" ref="lowerSlider" id="slider-min"
                        className="price-range-input lower-range"
                        min={this.props.lowerRangeLimit}
                        max={this.props.upperRangeLimit}
                        value={this.state.currentMinValue}
                        onChange={(evt) => { this._onLowerRangeSliderChange(evt) }}
                        onMouseUp={(evt) => this._onSelectMinPrice(evt)}
                        aria-invalid={this.state.rangeErrorMessage && this.state.rangeErrorMessage.length > 0 ? true : false} />
                    <label htmlFor="slider-max" className="visually-hidden">{this.props.filterPriceRangeMax}</label>
                    <input role="slider" type="range" ref="highSlider" id="slider-max"
                        className="price-range-input upper-range"
                        value={this.state.currentMaxValue}
                        min={this.props.lowerRangeLimit}
                        max={this.props.upperRangeLimit}
                        onChange={(evt) => { this._onUpperRangeSliderChange(evt) }}
                        onMouseUp={(evt) => this._onSelectMaxPrice(evt)}
                        aria-invalid={this.state.rangeErrorMessage && this.state.rangeErrorMessage.length > 0 ? true : false} />
                </div>


                <div className="price-range-numbers" aria-hidden="true">
                    <div className="price-range-numbers-wrapper">
                        <label htmlFor="priceRangeMin" data-currency-symbol={currencySymbol} className="price-range-numbers-label">
                            {this.props.filterPriceRangeMin}<span className="visually-hidden"> {this.props.filterPriceRangeTitle}</span>
                        </label>
                        <input type="number" pattern="\d*" id="priceRangeMin" className="price-range-numbers-input"
                            value={this.state.minTextBoxValue}
                            min={this.props.lowerRangeLimit}
                            max={this.props.upperRangeLimit}
                            onChange={(evt) => { this._onLowerRangeInputChangeBuffer(evt) }}
                            aria-invalid={this.state.rangeMinError && this.state.rangeErrorMessage.length > 0 ? true : false}
                        />
                    </div>
                    <div className="price-range-numbers-wrapper">
                        <label htmlFor="priceRangeMax" data-currency-symbol={currencySymbol} className="price-range-numbers-label">
                            {this.props.filterPriceRangeMax}<span className="visually-hidden"> {this.props.filterPriceRangeTitle}</span>
                        </label>
                        <input type="number" pattern="\d*" id="priceRangeMax" className="price-range-numbers-input"
                            value={this.state.maxTextBoxValue}
                            onChange={(evt) => { this._onUpperRangeInputChangeBuffer(evt) }}
                            min={this.props.lowerRangeLimit}
                            max={this.props.upperRangeLimit}
                            aria-invalid={this.state.rangeMaxError && this.state.rangeErrorMessage.length > 0 ? true : false}
                        />
                    </div>
                </div>
                {
                    this.state.rangeErrorMessage ?
                        <div className="price-range-numbers-input-error">
                            <svg dangerouslySetInnerHTML={{ __html: errorSvg }} />
                            <span>{this.state.rangeErrorMessage}</span>
                        </div>
                        : null
                }
            </>
        );
    }

    _onPriceRangeClick(evt: React.MouseEvent<any>) {
        const container = document.getElementsByClassName("price-range-container")[0];

        const priceRangeWidth = container.clientWidth;
        const clickedX = evt.clientX;
        const elementLeftX = container.getBoundingClientRect().left;

        // The lower and upper input range elements.
        const lowerElement: HTMLInputElement = container.getElementsByClassName("lower-range")[0] as HTMLInputElement;
        const upperElement: HTMLInputElement = container.getElementsByClassName("upper-range")[0] as HTMLInputElement;
        const max = +upperElement.max;
        const min = +lowerElement.min;

        // Where the bar was clicked %-wise from the left end.
        const clickPercent = (clickedX - elementLeftX) / priceRangeWidth;
        const lowerPercent = (+lowerElement.value - min) / (max - min);
        const upperPercent = (+upperElement.value - min) / (max - min);

        const upperDist = Math.abs(clickPercent - upperPercent);
        const lowerDist = Math.abs(clickPercent - lowerPercent);

        // If the click is closer to the upper value.
        if (upperDist < lowerDist) {
            upperElement.style.zIndex = `11`;
            lowerElement.style.zIndex = null;
        } else {
            upperElement.style.zIndex = null;
            lowerElement.style.zIndex = `11`;
        }
    }

    _onPriceRangeClear(evt) {
        evt.preventDefault();

        this.setState({
            currentMinValue: this.props.lowerRangeLimit,
            currentMaxValue: this.props.upperRangeLimit,
            minTextBoxValue: this.props.lowerRangeLimit,
            maxTextBoxValue: this.props.upperRangeLimit
        });

        this.props.onPriceRangeClear(this.props.lowerRangeLimit, this.props.upperRangeLimit);
    }

    //Slider changes
    _onLowerRangeSliderChange(evt) {
        this._errorMessageReset();
        let minValue = (evt.target as any).valueAsNumber > this.state.currentMaxValue - this.state.rangeDifference ? this.state.currentMaxValue - this.state.rangeDifference : (evt.target as any).valueAsNumber;
        this.setState({
            currentMinValue: minValue,
            minTextBoxValue: minValue
        });

        this.props.onSelectMinPrice(minValue);
    }

    _onSelectMinPrice(evt) {
        let minValue = this.state.currentMinValue;
        //if ie 11 or edge
        if (navigator.userAgent.match(/Trident.*rv:11\./) || navigator.userAgent.match(/Edge/)) {
            minValue = (evt.target as any).valueAsNumber > this.state.currentMaxValue - this.state.rangeDifference ? this.state.currentMaxValue - this.state.rangeDifference : (evt.target as any).valueAsNumber;
            this.setState({
                currentMinValue: minValue,
                minTextBoxValue: minValue
            });
        }
        (evt.target as any).blur();

        this.props.onSelectMinPrice(minValue);
    }

    _onSelectMaxPrice(evt) {
        let maxValue = this.state.currentMaxValue;
        //if ie 11 or edge
        if (navigator.userAgent.match(/Trident.*rv:11\./) || navigator.userAgent.match(/Edge/)) {
            maxValue = (evt.target as any).valueAsNumber < this.state.currentMinValue + this.state.rangeDifference ? this.state.currentMinValue + this.state.rangeDifference : (evt.target as any).valueAsNumber;
            this.setState({
                currentMaxValue: maxValue,
                maxTextBoxValue: maxValue
            });
        }
        (evt.target as any).blur();

        this.props.onSelectMaxPrice(maxValue);
    }

    _onUpperRangeSliderChange(evt) {
        this._errorMessageReset();
        let maxValue = (evt.target as any).valueAsNumber < this.state.currentMinValue + this.state.rangeDifference ? this.state.currentMinValue + this.state.rangeDifference : (evt.target as any).valueAsNumber;
        this.setState({
            currentMaxValue: maxValue,
            maxTextBoxValue: maxValue
        });

        this.props.onSelectMaxPrice(maxValue);
    }

    //Lower Range Input box Changes

    _onLowerRangeInputChangeBuffer(evt) {
        // ensures persistence of React synthetic event for debounced functions
        evt.persist();

        let textboxValue = (evt.target as any).valueAsNumber;

        //if ie 11 or edge
        if (navigator.userAgent.match(/Trident.*rv:11\./) || navigator.userAgent.match(/Edge/)) {
            textboxValue = +(evt.target as any).value;
        }

        this.setState({
            minTextBoxValue: textboxValue
        });

        this._onLowerRangeInputChange(evt);
    }

    _onLowerRangeInputChange(evt) {
        this._errorMessageReset();
        // access to evt present due evt.persist
        let minValue = Math.floor((evt.target as any).valueAsNumber);
        //if ie 11 or edge
        if (navigator.userAgent.match(/Trident.*rv:11\./) || navigator.userAgent.match(/Edge/)) {
            minValue = Math.floor(+(evt.target as any).value);
        }

        if (minValue < this.props.lowerRangeLimit) {
            this.setState({
                rangeErrorMessage: 'Minimum price cannot be less than $' + this.props.lowerRangeLimit,
                minTextBoxValue: this.props.lowerRangeLimit,
                currentMinValue: this.props.lowerRangeLimit,
                rangeMinError: true
            });
        } else if (minValue > this.state.currentMaxValue - this.state.rangeDifference) {
            this.setState({
                rangeErrorMessage: 'Minimum price cannot be greater than $' + (this.state.currentMaxValue - this.state.rangeDifference),
                minTextBoxValue: this.state.currentMaxValue - this.state.rangeDifference,
                currentMinValue: this.state.currentMaxValue - this.state.rangeDifference,
                rangeMinError: true,
            });
        } else {
            this.setState({
                minTextBoxValue: minValue,
                currentMinValue: minValue,
            });
        }

        this.props.onSelectMinPrice(this.state.currentMinValue);
    }

    //Upper Range Input box Changes

    _onUpperRangeInputChangeBuffer(evt) {
        evt.persist();

        let textboxValue = (evt.target as any).valueAsNumber;
        //if ie 11 or edge
        if (navigator.userAgent.match(/Trident.*rv:11\./) || navigator.userAgent.match(/Edge/)) {
            textboxValue = +(evt.target as any).value;
        }

        this.setState({
            maxTextBoxValue: textboxValue
        });
        this._onUpperRangeInputChange(evt);
    }

    _onUpperRangeInputChange(evt) {
        this._errorMessageReset();

        let maxValue = Math.ceil((evt.target as any).valueAsNumber);
        //if ie 11 or edge
        if (navigator.userAgent.match(/Trident.*rv:11\./) || navigator.userAgent.match(/Edge/)) {
            maxValue = Math.ceil(+(evt.target as any).value);
        }

        if (maxValue > this.props.upperRangeLimit) {
            this.setState({
                rangeErrorMessage: 'Maximum price cannot be greater than $' + this.props.upperRangeLimit,
                maxTextBoxValue: this.props.upperRangeLimit,
                currentMaxValue: this.props.upperRangeLimit,
                rangeMaxError: true

            });
        } else if (maxValue < this.state.currentMinValue + this.state.rangeDifference) {
            this.setState({
                rangeErrorMessage: 'Maximum price cannot be less than $' + (this.state.currentMinValue + this.state.rangeDifference),
                maxTextBoxValue: this.state.currentMinValue + this.state.rangeDifference,
                currentMaxValue: this.state.currentMinValue + this.state.rangeDifference,
                rangeMaxError: true

            });
        } else {
            this.setState({
                maxTextBoxValue: maxValue,
                currentMaxValue: maxValue,

            });
        }

        this.props.onSelectMaxPrice(this.state.currentMaxValue);
    }

    _errorMessageReset() {
        this.setState({
            ...this.state,
            rangeErrorMessage: '',
            rangeMaxError: false,
            rangeMinError: false
        });
    }

    componentWillReceiveProps(nextProps: IPriceRangeProps) {
        if (this.state.currentMinValue <= 0) {
            this.setState({
                ...this.state,
                currentMinValue: nextProps.lowerRangeLimit,
                currentMaxValue: nextProps.upperRangeLimit,
                minTextBoxValue: nextProps.lowerRangeLimit,
                maxTextBoxValue: nextProps.upperRangeLimit,
                lowerRangeLimit: nextProps.lowerRangeLimit,
                upperRangeLimit: nextProps.upperRangeLimit,
                rangeDifference: Math.ceil((nextProps.rangeMinSpread / 100) * (nextProps.upperRangeLimit - nextProps.lowerRangeLimit))
            } as any);
        } else {
            let needUpdate = false;
            let newUpperLimit = this.props.upperRangeLimit;
            let newLowerLimit = this.props.lowerRangeLimit;

            if (nextProps.lowerRangeLimit < this.props.lowerRangeLimit
                || nextProps.lowerRangeLimit > this.state.currentMinValue) {
                needUpdate = true;
                this.props.onSelectMinPrice(nextProps.lowerRangeLimit);
                newLowerLimit = nextProps.lowerRangeLimit;
            }
            if (nextProps.upperRangeLimit > this.props.upperRangeLimit
                || nextProps.upperRangeLimit < this.state.currentMaxValue) {
                needUpdate = true;
                this.props.onSelectMaxPrice(nextProps.upperRangeLimit);
                newUpperLimit = nextProps.upperRangeLimit;
            }

            if (needUpdate) {
                this.setState({
                    ...this.state,
                    currentMinValue: newLowerLimit,
                    currentMaxValue: newUpperLimit,
                    minTextBoxValue: newLowerLimit,
                    maxTextBoxValue: newUpperLimit,
                    lowerRangeLimit: newLowerLimit,
                    upperRangeLimit: newUpperLimit,
                    rangeDifference: Math.ceil((nextProps.rangeMinSpread / 100) * (nextProps.upperRangeLimit - nextProps.lowerRangeLimit))
                } as any);
            } else {
                this.setState({
                    rangeDifference: Math.ceil((nextProps.rangeMinSpread / 100) * (nextProps.upperRangeLimit - nextProps.lowerRangeLimit))
                } as any);
            }
        }
    }
}

export default PriceRange;